import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import type { StyleClasses } from '@paintscout/ui/styles'
import { Button, Hidden, Paper, PopupMenu, PopupMenuItem } from '@paintscout/ui'
import { useUser } from '../../providers/UserProvider'

const useStyles = makeStyles<Theme, DocsContainerProps>(
  (theme) => ({
    root: {},
    contactDocsContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start'
    },
    contactDocsHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      paddingTop: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    contactDocsBody: {
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    contactDocsButtonContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      width: '100%',
      alignItems: 'center',
      gap: theme.spacing(2),
      overflow: 'hidden',
      overflowX: 'auto',
      paddingBottom: theme.spacing(2)
    },
    newButton: {
      marginLeft: theme.spacing(),
      marginTop: `-${theme.spacing(0.75)}px`
    }
  }),
  { name: 'DocsContainer' }
)

export interface DocsContainerProps {
  classes?: StyleClasses<typeof useStyles>
  tabs: {
    label: string
    value: string
    active: boolean
    allowNew?: boolean
  }[]
  handleTabClick: (value: string) => void
  handleNewClick?: (vals: { type: string }) => void
  children: React.ReactNode
}

function DocsContainer(props: DocsContainerProps) {
  const classes = useStyles(props)
  const { tabs, handleTabClick, handleNewClick, children } = props
  const { isSubscribed, trialExpired } = useUser()
  const hasSomeNew = tabs.some((tab) => tab.allowNew ?? true)

  return (
    <Paper className={classes.contactDocsContainer}>
      <div className={classes.contactDocsHeader}>
        <div className={classes.contactDocsButtonContainer}>
          {tabs.map((tab) => (
            <div key={tab.value}>
              <Button variant="tab" onClick={() => handleTabClick(tab.value)} size="small" active={tab.active}>
                {tab.label}
              </Button>
            </div>
          ))}
        </div>
        {handleNewClick && hasSomeNew && (
          <Hidden smDown>
            <PopupMenu
              component={
                <Button
                  className={classes.newButton}
                  variant="hoverUnderline"
                  color="primary"
                  startIcon={<AddIcon />}
                  disabled={!isSubscribed || trialExpired}
                >
                  New
                </Button>
              }
              id="popup-menu"
            >
              {tabs
                .filter((tab) => !!(tab?.allowNew ?? true))
                .map((tab) => (
                  <PopupMenuItem
                    key={tab.value}
                    onClick={() => {
                      handleNewClick({ type: tab.value })
                    }}
                  >
                    {tab.label.endsWith('s') ? tab.label.substring(0, tab.label.length - 1) : tab.label}
                  </PopupMenuItem>
                ))}
            </PopupMenu>
          </Hidden>
        )}
      </div>
      <div className={classes.contactDocsBody}>{children}</div>
    </Paper>
  )
}

export default DocsContainer
